import cn from 'classnames';
import React, { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { getBooleans, isBoolean } from './get_booleans';
import { getColors, isColor } from './get_colors';
import { getSizes, isSize } from './get_sizes';
import { getSpecials, isSpecial } from './get_specials';
import { getStringsNumbers, isNumber, isString } from './get_strings_numbers';
import './index.css';

interface SCViewRest {
  flex?: boolean;
  flex1?: boolean;
  flexCol?: boolean;
  flexRow?: boolean;
  flexWrap?: boolean;
  gap?: number;

  justifyCenter?: boolean;
  justifyEnd?: boolean;
  justifyStart?: boolean;
  justifyBetween?: boolean;
  justifyAround?: boolean;

  itemsStart?: boolean;
  itemsCenter?: boolean;
  itemsEnd?: boolean;

  wScreen?: boolean;
  hScreen?: boolean;
  wFull?: boolean;
  hFull?: boolean;
  w?: number | string;
  h?: number | string;
  maxW?: number | string;
  maxH?: number | string;

  p?: number | string;
  pt?: number | string;
  pb?: number | string;
  pl?: number | string;
  pr?: number | string;
  px?: number | string;
  py?: number | string;

  m?: number | string;
  mt?: number | string;
  mb?: number | string;
  ml?: number | string;
  mr?: number | string;
  mx?: number | string;
  my?: number | string;

  bg?: string;
  bgWhite?: boolean;
  bgMain?: boolean;
  bgGray100?: boolean;
  bgGray300?: boolean;

  rounded?: boolean;
  roundedFull?: boolean;

  overflowAuto?: boolean;
  overflowYAuto?: boolean;
  overflowXAuto?: boolean;
  overflowHidden?: boolean;

  relative?: boolean;
  absolute?: boolean;
  sticky?: boolean;
  top?: number | string;
  left?: number | string;
  right?: number | string;
  bottom?: number | string;

  hidden?: boolean;
  visible?: boolean;
  invisible?: boolean;

  cursorNotAllowed?: boolean;
  cursorPointer?: boolean;
  cursorGrab?: boolean;
  selectNone?: boolean;

  z?: number;

  fontBold?: boolean;
  // *** theme color ***
  textWhite?: boolean;
  textNormal?: boolean;
  textDesc?: boolean;
  textMain?: boolean;
  textPrimary?: boolean;
  textPrimaryHover?: boolean;
  textWarning?: boolean;
  textError?: boolean;
  textLink?: boolean;

  // *** theme size ***
  textXs?: boolean;
  textSm?: boolean;
  textMd?: boolean;
  textLg?: boolean;
  textXl?: boolean;
  textXxl?: boolean;
  textXxxl?: boolean;

  // *** special truncate ***
  truncate?: boolean;

  // *** special border ***
  border?: boolean;
  borderT?: boolean;
  borderB?: boolean;
  borderL?: boolean;
  borderR?: boolean;

  duration?: string;
  rotate?: string;
  rotate180?: boolean;
  transitionTransform?: boolean;
}

interface SCViewProps
  extends SCViewRest,
    Pick<
      React.HTMLAttributes<HTMLDivElement>,
      'className' | 'style' | 'children' | 'onClick' | 'title' | 'draggable' | 'onDragStart'
    > {
  Component?: React.ElementType;
  hover?: SCViewRest;

  // do not use this prop。 button type
  type?: any;
  onSubmit?: any;
}

const SCView = memo(
  forwardRef((props: SCViewProps, ref: any) => {
    const {
      Component = 'div',
      onClick,
      className,
      style,
      draggable,
      onDragStart,
      children,
      hover,
      type,
      onSubmit,
      ...originRest
    } = props;

    const [isHover, setIsHover] = useState(false);

    const rest = useMemo(() => {
      return {
        ...originRest,
        ...(isHover ? hover : {}),
      };
    }, [isHover, hover, originRest]);

    const { specials, colors, sizes, booleans, strings, numbers } = useMemo(() => {
      const specials: string[] = [];
      const colors: string[] = [];
      const sizes: string[] = [];
      const booleans: string[] = [];
      const strings: string[] = [];
      const numbers: string[] = [];

      Object.keys(rest).forEach((key) => {
        if (isSpecial(key)) {
          if (rest[key]) {
            specials.push(key);
          }
        } else if (isColor(key)) {
          if (rest[key]) {
            colors.push(key);
          }
        } else if (isSize(key)) {
          if (rest[key]) {
            sizes.push(key);
          }
        } else if (isBoolean(rest[key])) {
          booleans.push(key);
        } else if (isString(rest[key])) {
          strings.push(key);
        } else if (isNumber(rest[key])) {
          numbers.push(key);
        }
      });

      return { specials, colors, sizes, booleans, strings, numbers };
    }, [rest]);

    const { booleanClassName } = getBooleans({ booleans });
    const c = cn('sce-view', booleanClassName, className);

    const { colorStyle } = getColors({ colors });
    const { sizeStyle } = getSizes({ sizes });
    const { specialStyle } = getSpecials({ specials });
    const { stringNumberStyle } = getStringsNumbers({ strings, numbers, rest });

    const s = {
      ...colorStyle,
      ...sizeStyle,
      ...specialStyle,
      ...stringNumberStyle,
      ...style,
    };

    const handleMouseEnter = useCallback(() => {
      if (!!hover && !isHover) {
        setIsHover(true);
      }
    }, [hover, isHover]);

    const handleMouseLeave = useCallback(() => {
      if (!!hover && isHover) {
        setIsHover(false);
      }
    }, [hover, isHover]);

    return (
      <Component
        ref={ref}
        className={c}
        style={s}
        onClick={onClick}
        draggable={draggable}
        onDragStart={onDragStart}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        type={type}
        onSubmit={onSubmit}
      >
        {children}
      </Component>
    );
  }),
);

export { SCView };
export type { SCViewProps };
